import { MicrophoneOff01Icon } from "Icons/MicrophoneOff01";
import { Recording02Icon } from "Icons/Recording02";

import { useEffect, useRef } from "react";

import { css } from "@emotion/react";

import { Button } from "components/shared/library/Button";
import { Image } from "components/shared/library/Image";
import RibbonLogo from "components/shared/library/RibbonLogo";
import { Typography } from "components/shared/library/Typography";

import { useAccount } from "contexts/AccountContext";
import { useInterview } from "contexts/InterviewContext";
import { useWhiteLabel } from "contexts/WhiteLabelContext";

import { getAvatarPath } from "utils/file";
import { down } from "utils/mediaQueryStrings";

import { gradient, pink, purple, uiGray, white } from "constants/colors";
import { defaultLiveCallAvatar, placeholderAvatar } from "constants/images";

import { MuteButtons } from "./MuteButtons";

const style = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    margin: 16,
    width: "-webkit-fill-available",
    background: white,
    borderRadius: 16,
    boxShadow:
      " 0px 20px 24px -4px rgba(27, 27, 27, 0.08), 0px 8px 8px -4px rgba(27, 27, 27, 0.03)",
  }),
  header: css({
    display: "flex",
    borderBottom: `1px solid ${uiGray[10]}`,
    padding: "24px 40px",
    justifyContent: "space-between",
    gap: 8,
  }),
  logo: css({
    display: "flex",
    gap: 8,
  }),
  disclaimer: css({
    display: "flex",
    padding: "24px 40px",
    maxWidth: 510,
  }),
  content: css({
    display: "flex",
    padding: "24px 40px",
    gap: 24,
    [down("mobile")]: {
      flexDirection: "column",
      alignItems: "center",
      padding: 16,
      gap: 16,
    },
  }),
  footer: css({
    display: "flex",
    borderTop: `1px solid ${uiGray[10]}`,
    padding: "24px 40px",
    gap: 8,
  }),
  flex1: css({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
  }),
  imgBackground: css({
    position: "relative",
    height: 252,
    width: 252,
    borderRadius: 16,
    border: `1px solid ${purple[50]}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    [down("mobile")]: { height: 164, width: 164 },
  }),
  company: css({
    background: purple[50],
  }),
  img: css({
    borderRadius: 16,
    height: 140,
    width: 140,
    overflow: "hidden",
  }),
  candidateImg: css({
    boxShadow:
      "0px 12px 16px -4px rgba(27, 27, 27, 0.08), 0px 4px 6px -2px rgba(27, 27, 27, 0.03)",
  }),
  focus: css({
    border: `1px solid ${purple[600]}`,
    boxShadow: `0px 0px 0px 4px ${purple.focusShadow}`,
  }),
  badge: css({
    width: 120,
    height: 48,
    display: "flex",
    gap: 8,
    background: pink[100],
    borderRadius: 24,
    alignItems: "center",
    justifyContent: "start",
  }),
  dot: css({
    marginLeft: 32,
    width: 8,
    height: 8,
    borderRadius: 4,
    background: pink[600],
    boxShadow: "0px 0px 0px 4px rgba(243, 68, 121, 0.30)",
  }),
  speakingIcon: css({
    position: "absolute",
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: gradient.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bottom: 8,
    left: 8,
  }),
};

export const LiveCall = ({
  counter,
  handleEndCall,
  videoStream,
  isAgentTalking,
  toggleAudioMute,
  toggleVideoPause,
  isAudioMuted,
  isVideoPaused,
}: {
  counter: string;
  handleEndCall: () => void;
  videoStream?: MediaStream;
  audioStream?: MediaStream;
  isAgentTalking: boolean;
  toggleAudioMute: () => void;
  toggleVideoPause: () => void;
  isAudioMuted: boolean;
  isVideoPaused: boolean;
}) => {
  const { interviewDetails } = useInterview();
  const { accountProfile } = useAccount(false); // user is authed, but can be anonymously signed in with the API flow. In that case, there is no account profile
  const videoRef = useRef<HTMLVideoElement>(null);

  // If there is a video stream, we should play it
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = videoStream || null;
    }
  }, [videoStream]);

  const isCandidateTalking = !isAgentTalking;
  const { interviewStyle } = useWhiteLabel(true);

  return (
    <div css={style.wrapper}>
      <div css={style.header}>
        {!interviewStyle && (
          <div css={style.logo}>
            <RibbonLogo />
          </div>
        )}
      </div>
      <div css={style.content}>
        <div
          data-testid="agent-background"
          css={[
            style.imgBackground,
            style.company,
            isAgentTalking ? style.focus : undefined,
          ]}
        >
          <div css={style.img}>
            <Image
              src={
                interviewDetails?.companyLogo
                  ? getAvatarPath(interviewDetails?.companyLogo)
                  : defaultLiveCallAvatar
              }
              alt={`${interviewDetails?.orgName || "Org"}'s logo`}
              width={140}
              height={140}
            />
          </div>
          {isAgentTalking && (
            <div css={style.speakingIcon} data-testid="agent-talking">
              <Recording02Icon stroke={white} width={16} height={16} />
            </div>
          )}
        </div>
        <div
          data-testid="candidate-background"
          css={[
            style.imgBackground,
            isCandidateTalking ? style.focus : undefined,
          ]}
        >
          {videoStream ? (
            <video
              ref={videoRef}
              autoPlay
              height={252}
              aria-label="Video preview"
              playsInline
            />
          ) : (
            <div css={[style.img, style.candidateImg]}>
              <Image
                src={getAvatarPath(
                  accountProfile?.avatarUrl || placeholderAvatar
                )}
                alt={"Recruit AI logo"}
                width={140}
                height={140}
              />
            </div>
          )}
          {isCandidateTalking && !isAudioMuted && (
            <div css={style.speakingIcon} data-testid="candidate-talking">
              <Recording02Icon stroke={white} width={16} height={16} />
            </div>
          )}
          {isCandidateTalking && isAudioMuted && (
            <div css={style.speakingIcon} data-testid="candidate-talking">
              <MicrophoneOff01Icon stroke={white} width={16} height={16} />
            </div>
          )}
        </div>
      </div>
      <div css={style.footer}>
        <MuteButtons
          toggleAudioMute={toggleAudioMute}
          toggleVideoPause={toggleVideoPause}
          isAudioMuted={isAudioMuted}
          isVideoPaused={isVideoPaused}
        />
        <div css={style.flex1}>
          <div css={style.badge}>
            <div css={style.dot} />
            <Typography color={pink[700]} weight="bold">
              {counter}
            </Typography>
          </div>
        </div>

        <Button color="red" onClick={handleEndCall} size="large">
          End call
        </Button>
      </div>
    </div>
  );
};
